import * as React from "react";
import { StyleSheet } from "react-native";
import Colors from "../../constants/Colors";
import { Feather } from "@expo/vector-icons";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { Modalize } from "react-native-modalize";
import {
  Input,
  CheckBox,
  Dropdown,
  View,
  Text,
  MaskedInput,
  MaskedTextInput,
} from "../../components/Themed";
import useThemeColor from "../../hooks/useThemeColor";
import { useForm } from "react-hook-form";
import Layout from "../../constants/Layout";
import { Masks } from "react-native-mask-input";
import GoogleAutocomplete from "../../components/GoogleAutocomplete";
import { useRef } from "react";
import { getForms } from "../../services/mock";
import Pressable from "../../components/Pressable";

type Props = {
  show: boolean;
  isEditMode?: boolean;
  floatingComponent?: any;
  data?: any;
  onSubmit: (data: any) => void;
  onDeletePressed?: () => void;
  onClosed: () => void;
};

export default function CreateEditOpenHouse(props: Props) {
  const {
    show,
    isEditMode = false,
    floatingComponent,
    data,
    onSubmit,
    onDeletePressed,
    onClosed,
  } = props;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    setError,
    watch,
    getValues,
  } = useForm();

  const [forms, setForms] = React.useState<any[]>([]);

  const createOpenHouseModal = useRef<Modalize>(null);
  const googleAutoComplete = useRef();

  const showingAgenData = [
    { value: 1, text: "Agent 1" },
    { value: 2, text: "Agent 2" },
    { value: 3, text: "Agent 3" },
  ];

  const tagsData = [
    { value: 1, text: "Tag 1" },
    { value: 1, text: "Tag 2" },
    { value: 1, text: "Tag 3" },
  ];

  React.useEffect(() => {}, []);

  React.useEffect(() => {
    if (data) {
      setValue("form", data?.form);
      googleAutoComplete?.current?.setAddressText("data?.address");
      setValue("importListingDetails", data?.importListingDetails);
      setValue("address", data?.address);
      setValue("city", data?.city);
      setValue("state", data?.state);
      setValue("zip", data?.zip);
      setValue("date", data?.date);
      setValue("timeStart", data?.timeStart);
      setValue("timeEnd", data?.timeEnd);
      setValue("host", data?.host);
      setValue(
        "hostReceivesReportingDataAndLeads",
        data?.hostReceivesReportingDataAndLeads
      );
      setValue("showingAgent", data?.showingAgent);
      setValue("tags", data?.tags);
      setValue("notes", data?.notes);
    }
  }, [data]);

  React.useEffect(() => {
    if (show) {
      createOpenHouseModal?.current?.open();

      getForms().then((forms) => {
        const allForms = forms.map((form) => {
          return {
            value: form.id,
            text: form.formName,
          };
        });
        setForms(allForms);
      });
    } else {
      createOpenHouseModal?.current?.close();
    }
  }, [show]);

  const autocompletePress = (data, details) => {
    const streetAddress = details.address_components.find(
      (x) => x.types[0] === "street_number"
    ).long_name;

    const route = details.address_components.find(
      (x) => x.types[0] === "route"
    ).long_name;

    const zipCode = details.address_components.find(
      (x) => x.types[0] === "postal_code"
    ).long_name;

    const city = details.address_components.find(
      (x) => x.types[0] === "locality"
    ).long_name;

    const state = details.address_components.find(
      (x) => x.types[0] === "administrative_area_level_1"
    ).short_name;

    setValue("address", `${streetAddress} ${route}`);
    setValue("city", city);
    setValue("state", state);
    setValue("zip", zipCode);
  };

  return (
    <Modalize
      onOpen={() => {
        if (!isEditMode) {
          reset();
        }
      }}
      ref={createOpenHouseModal}
      modalHeight={Layout.window.height * 0.9}
      withReactModal
      FloatingComponent={floatingComponent}
      scrollViewProps={{
        keyboardShouldPersistTaps: "always",
        scrollEnabled: true,
        showsVerticalScrollIndicator: false,
      }}
      childrenStyle={{
        paddingHorizontal: 20,
        backgroundColor: useThemeColor(
          { light: Colors.base_colors_white, dark: Colors.nuetral_grey_600 },
          "background"
        ),
      }}
      HeaderComponent={
        <View
          lightColor={Colors.tinted_grey_100}
          darkColor={Colors.nuetral_grey_700}
          style={{
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            paddingLeft: 32,
            height: 84,
            justifyContent: "center",
          }}
        >
          <Text
            size={18}
            lineHeight={24}
            weight={"Bold"}
            lightColor={Colors.nuetral_grey_700}
          >
            {isEditMode ? "Edit" : "Create"} Open House
          </Text>
        </View>
      }
      FooterComponent={
        <View
          lightColor={Colors.base_colors_white}
          darkColor={Colors.nuetral_grey_700}
          style={{
            height: 80,
            flexDirection: "row",
            borderTopColor: Colors.tinted_grey_300,
            borderTopWidth: 1,
          }}
        >
          <Pressable
            style={{
              flex: 1,
            }}
            onPress={() => {
              onClosed();
              createOpenHouseModal?.current?.close();
            }}
          >
            <View
              style={{
                flex: 1,
                flexDirection: "row",
                alignItems: "center",
                alignSelf: "center",
              }}
            >
              <Feather name="x" color={Colors.nuetral_grey_700} size={14} />
              <Text lightColor={Colors.nuetral_grey_700}>Cancel</Text>
            </View>
          </Pressable>
          <Pressable
            style={{
              flex: 1,
              borderLeftColor: Colors.tinted_grey_300,
              borderLeftWidth: 1,
            }}
            onPress={handleSubmit(onSubmit)}
          >
            <View
              style={{
                flex: 1,
                flexDirection: "row",
                alignItems: "center",
                alignSelf: "center",
              }}
            >
              <Feather name="save" color={Colors.primary_500} size={14} />
              <Text lightColor={Colors.primary_500}>Save</Text>
            </View>
          </Pressable>
        </View>
      }
    >
      <KeyboardAwareScrollView
        style={{
          flex: 1,
          gap: 16,
          backgroundColor: "transparent",
        }}
        contentContainerStyle={{
          gap: 16,
        }}
        showsVerticalScrollIndicator={false}
        // enableAutomaticScroll={true}
        extraScrollHeight={300}
        extraHeight={200}
        keyboardShouldPersistTaps="always"
      >
        {/* <Dropdown
            style={{ zIndex: 10 }}
            data={forms}
            placeholder="Select Form"
            lightColor={Colors.tinted_grey_100}
            darkColor={Colors.nuetral_grey_700}
            name="form"
            control={control}
            errors={errors}
            required={false}
            label="Select a form"
            setValue={setValue}
          ></Dropdown> */}

        <Text lightColor={Colors.nuetral_grey_700}>
          Import a listing or fill out the information below manually.
        </Text>

        <GoogleAutocomplete
          ref={googleAutoComplete}
          onLocationSelected={autocompletePress}
          placeholder={"Search for a listing address to auto-populate"}
          leftIcon={false}
          rightIcon={false}
        />

        <View
          style={{ borderTopColor: Colors.tinted_grey_300, borderTopWidth: 1 }}
        ></View>

        <Input
          placeholder="Address"
          label="Address"
          lightColor={Colors.tinted_grey_100}
          darkColor={Colors.nuetral_grey_700}
          control={control}
          errors={errors}
          name="address"
          required={true}
          errorMessage="Please enter a valid address"
        />
        <Input
          placeholder="City"
          label="City"
          lightColor={Colors.tinted_grey_100}
          darkColor={Colors.nuetral_grey_700}
          control={control}
          errors={errors}
          name="city"
          required={true}
          errorMessage="Please enter a valid city"
        />

        <View
          style={{
            flexDirection: "row",
            gap: 16,
          }}
        >
          <View style={{ flex: 1 }}>
            <Input
              placeholder="State"
              label="State"
              lightColor={Colors.tinted_grey_100}
              darkColor={Colors.nuetral_grey_700}
              control={control}
              errors={errors}
              name="state"
              required={true}
              errorMessage="Please enter a valid state"
            />
          </View>
          <View style={{ flex: 1 }}>
            <MaskedTextInput
              mask={[/\d/, /\d/, /\d/, /\d/, /\d/]}
              rules={{ required: true, minLength: 5 }}
              placeholder="Zip"
              label="Zip"
              lightColor={Colors.tinted_grey_100}
              darkColor={Colors.nuetral_grey_700}
              control={control}
              errors={errors}
              name="zip"
              keyboardType="phone-pad"
              errorMessage="Please enter a valid zip code"
            />
          </View>
        </View>

        <View
          style={{ borderTopColor: Colors.tinted_grey_300, borderTopWidth: 1 }}
        ></View>

        <MaskedTextInput
          mask={Masks.DATE_MMDDYYYY}
          rules={{ required: true, minLength: 10 }}
          keyboardType={"numeric"}
          label="Date"
          lightColor={Colors.tinted_grey_100}
          darkColor={Colors.nuetral_grey_700}
          control={control}
          errors={errors}
          name="date"
          errorMessage="Please enter a valid date"
        />

        <View
          style={{
            flexDirection: "row",
            gap: 16,
          }}
        >
          <View style={{ flex: 1 }}>
            <MaskedTextInput
              mask={[/[0-1]/, /\d/, ":", /[0-5]/, /\d/]}
              rules={{ required: true, minLength: 5 }}
              keyboardType={"numeric"}
              // placeholder="Time"
              label="Time"
              lightColor={Colors.tinted_grey_100}
              darkColor={Colors.nuetral_grey_700}
              control={control}
              errors={errors}
              name="timeStart"
              required={true}
              errorMessage="Please enter a valid time"
            />
          </View>
          <View style={{ flex: 1 }}>
            <MaskedTextInput
              mask={[/[0-1]/, /\d/, ":", /[0-5]/, /\d/]}
              rules={{ required: true, minLength: 5 }}
              keyboardType={"numeric"}
              label="Time"
              lightColor={Colors.tinted_grey_100}
              darkColor={Colors.nuetral_grey_700}
              control={control}
              errors={errors}
              name="timeEnd"
              required={false}
            />
          </View>
        </View>

        <View>
          <Input
            placeholder="Host"
            label="Host"
            lightColor={Colors.tinted_grey_100}
            darkColor={Colors.nuetral_grey_700}
            control={control}
            errors={errors}
            name="host"
            required={false}
          />
          <CheckBox
            style={{ marginTop: 8 }}
            label={"Host receives reporting data and leads"}
            control={control}
            errors={errors}
            name="hostReceivesReportingDataAndLeads"
            required={false}
          ></CheckBox>
        </View>

        <Dropdown
          data={showingAgenData}
          style={{ zIndex: 1002, elevation: 1002 }}
          placeholder="Host"
          lightColor={Colors.tinted_grey_100}
          darkColor={Colors.nuetral_grey_700}
          name="showingAgent"
          control={control}
          errors={errors}
          required={false}
          label="Host"
          setValue={setValue}
        ></Dropdown>

        <Dropdown
          data={tagsData}
          style={{ zIndex: 1001, elevation: 1001 }}
          placeholder="Search Tags"
          lightColor={Colors.tinted_grey_100}
          darkColor={Colors.nuetral_grey_700}
          name="tags"
          control={control}
          errors={errors}
          required={false}
          label="Tags"
          setValue={setValue}
          multiSelect={true}
        ></Dropdown>

        <Input
          style={{
            height: 100,
          }}
          placeholder="Enter message here..."
          label="Notes"
          lightColor={Colors.tinted_grey_100}
          darkColor={Colors.nuetral_grey_700}
          control={control}
          errors={errors}
          name="notes"
          required={false}
          multiline
          numberOfLines={4}
        />
        {isEditMode && (
          <Pressable
            style={styles.deleteOpenHouse}
            onPress={() => onDeletePressed && onDeletePressed()}
          >
            <Text size={16} weight="Bold" lightColor={Colors.semantic_red_100}>
              Delete Open House
            </Text>
          </Pressable>
        )}
      </KeyboardAwareScrollView>
    </Modalize>
  );
}

const styles = StyleSheet.create({
  container: {},
  deleteOpenHouse: {
    alignItems: "center",
    paddingVertical: 24,
  },
});
